/* eslint-disable @typescript-eslint/consistent-type-assertions */

import { type ContextFrom, createMachine } from 'xstate';
import { assign } from 'xstate/lib/actions';

import { getDefaultPaymentMethodId } from '../utils';
import {
  type PaymentMethod,
  type PointsReward,
  type ScanToPayMachineContext,
  type ScanToPayMachineEvents,
  type ScanToPayMachineServices,
} from './scan-to-pay.machine.types';

// ─────────────────────────────────────────────────────────────────────────────

export const createScanToPayMachine = () => {
  return createMachine(
    {
      // eslint-disable-next-line @typescript-eslint/consistent-type-imports
      tsTypes: {} as import('./scan-to-pay.machine.typegen').Typegen0,
      predictableActionArguments: true,
      preserveActionOrder: true,
      id: 'scan-to-pay-state-machine',
      schema: {
        context: {} as ScanToPayMachineContext,
        events: {} as ScanToPayMachineEvents,
        services: {} as ScanToPayMachineServices,
      },
      context: getInitialContext(),
      initial: 'waiting',
      states: {
        waiting: {
          id: 'scan-to-pay-waiting',
          description: 'Waiting for the machine to be initialized.',
        },
        fetching: {
          id: 'scan-to-pay-fetching',
          description: `Fetching the initial data.`,
          invoke: {
            src: 'fetchData',
            onError: '#scan-to-pay-failed',
            onDone: {
              actions: ['onFetchDataDone'],
              target: '#scan-to-pay-ready',
            },
          },
        },
        ready: {
          id: 'scan-to-pay-ready',
          description: `Initial data is ready.`,
          type: 'parallel',
          states: {
            standard: {
              initial: 'idle',
              states: {
                idle: {
                  id: 'idle',
                  description: 'Ready to receive events.',
                  on: {
                    REQUEST_ADD_PAYMENT: '#adding-payment-method',
                    REQUEST_PAYMENT_SELECTION: '#payment-selection',
                    REQUEST_LOYALTY_INFO: '#loyalty-info',
                    REQUEST_FAQ: '#faq',
                    SWITCH_USE_CREDIT: {
                      target: ['#idle', '#payment-token-fetching'],
                      actions: ['onSwitchUseCredit'],
                    },
                  },
                },
                addingPaymentMethod: {
                  id: 'adding-payment-method',
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
                paymentSelection: {
                  id: 'payment-selection',
                  on: {
                    SET_DEFAULT_PAYMENT_METHOD:
                      '#scan-to-pay-setting-default-payment-method',
                    REQUEST_ADD_PAYMENT: '#adding-payment-method',
                    CANCEL_REQUEST: '#idle',
                  },
                },
                settingDefaultPaymentMethod: {
                  id: 'scan-to-pay-setting-default-payment-method',
                  description: `Fetching the initial data.`,
                  invoke: {
                    src: 'setDefaultPaymentMethod',
                    onError: '#scan-to-pay-failed',
                    onDone: {
                      target: ['#idle', '#payment-token-fetching'],
                      actions: ['onSwitchPaymentMethod'],
                    },
                  },
                },
                loyaltyInfo: {
                  id: 'loyalty-info',
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
                faq: {
                  id: 'faq',
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
              },
            },
            identifyToken: {
              initial: 'fetching',
              states: {
                fetching: {
                  id: 'identify-token-fetching',
                  invoke: {
                    src: 'fetchIdentifyToken',
                    onError: '#scan-to-pay-failed',
                    onDone: {
                      target: 'polling',
                      actions: ['onFetchIdentifyTokenDone'],
                    },
                  },
                },
                polling: {
                  after: {
                    TOKEN_POLLING_DELAY: {
                      target: '#identify-token-fetching',
                    },
                  },
                },
              },
            },
            paymentToken: {
              initial: 'checking',
              states: {
                checking: {
                  always: {
                    target: 'fetching',
                    cond: 'shouldFetchPaymentToken',
                  },
                },
                fetching: {
                  id: 'payment-token-fetching',
                  invoke: {
                    src: 'fetchPaymentToken',
                    onError: '#scan-to-pay-failed',
                    onDone: {
                      target: 'polling',
                      actions: ['onFetchPaymentTokenDone'],
                    },
                  },
                },
                polling: {
                  after: {
                    TOKEN_POLLING_DELAY: {
                      target: '#payment-token-fetching',
                      cond: 'shouldFetchPaymentToken',
                    },
                  },
                },
              },
            },
            credits: {
              initial: 'polling',
              states: {
                fetching: {
                  id: 'credits-fetching',
                  invoke: {
                    src: 'fetchCredits',
                    onError: 'polling',
                    onDone: {
                      target: 'polling',
                      actions: ['onFetchCreditsDone'],
                    },
                  },
                },
                polling: {
                  on: {
                    REFETCH_CREDITS: 'fetching',
                  },
                  after: {
                    CREDITS_POLLING_DELAY: 'fetching',
                  },
                },
              },
            },
          },
        },
        failed: {
          id: 'scan-to-pay-failed',
          description: 'A failed state that awaits for another start event.',
        },
      },
      on: {
        START: '#scan-to-pay-fetching',
      },
    },
    {
      actions: {
        onFetchDataDone: assign({
          availablePoints: (_context, event) => event.data.availablePoints,
          availableCredit: (_context, event) => event.data.availableCredit,
          paymentMethods: (_context, event) => event.data.paymentMethods,
          pointsRewards: (_context, event) => event.data.pointsRewards,
          paymentMethodId: (_context, event) => getDefaultPaymentMethodId(event.data.paymentMethods), // prettier-ignore
        }),
        onFetchIdentifyTokenDone: assign({
          identifyToken: (_context, event) => event.data.identifyToken,
        }),
        onFetchPaymentTokenDone: assign({
          paymentToken: (_context, event) => event.data.paymentToken,
        }),
        onFetchCreditsDone: assign({
          availableCredit: (_context, event) => event.data.availableCredit,
        }),
        onSwitchPaymentMethod: assign({
          paymentMethodId: (_context, event) => event.data.paymentMethodId,
        }),
        onSwitchUseCredit: assign({
          useCredit: (context) => !context.useCredit,
        }),
      },
      services: {
        // External service to fetch the initial data.
        // eslint-disable-next-line functional/prefer-tacit
        async fetchData() {
          return getInitialContext();
        },
        // External service to fetch the identify token.
        async fetchIdentifyToken() {
          return {
            identifyToken: '',
          };
        },
        // External service to fetch the payment token.
        async fetchPaymentToken(_context) {
          return {
            paymentToken: '',
          };
        },
      },
      guards: {
        shouldFetchPaymentToken: (context) => {
          return Boolean(context.paymentMethodId);
        },
      },
      delays: {
        TOKEN_POLLING_DELAY: 120_000,
        CREDITS_POLLING_DELAY: 120_000,
      },
    },
  );
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const getInitialContext = (): ScanToPayMachineContext => {
  return {
    paymentMethods: [] as readonly PaymentMethod[],
    pointsRewards: [] as readonly PointsReward[],
    availableCredit: 0,
    availablePoints: 0,

    useCredit: true,
    paymentMethodId: '',
    identifyToken: '',
    paymentToken: '',
  };
};
// ─── Types ──────────────────────────────────────────────────────────────────

export type ScanToPayContext = ContextFrom<
  ReturnType<typeof createScanToPayMachine>
>;
